import type { CrawlUrlTreeNode } from './buildCrawlUrlTree';

export const MIN_DISPLAY_DEPTH = 1;
export const MAX_DISPLAY_DEPTH = 50;

export function collectRealDescendantIds(node: CrawlUrlTreeNode): string[] {
  const ids: string[] = [];
  if (!node.isSynthetic) {
    ids.push(node.id);
  }
  for (const child of node.children) {
    ids.push(...collectRealDescendantIds(child));
  }
  return ids;
}

/**
 * Total real (backend) nodes in the subtree below this node.
 * Excludes the node itself and UI-only synthetic parents.
 */
export function countDescendantNodes(node: CrawlUrlTreeNode): number {
  let count = 0;
  for (const child of node.children) {
    if (!child.isSynthetic) {
      count += 1;
    }
    count += countDescendantNodes(child);
  }
  return count;
}

export function collectExpandableNodeIds(nodes: CrawlUrlTreeNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.children.length > 0) {
      ids.push(node.id);
    }
    ids.push(...collectExpandableNodeIds(node.children));
  }
  return ids;
}

export function collectNodeIdsAtTreeDepth(
  nodes: CrawlUrlTreeNode[],
  targetDepth: number,
  currentDepth = 0,
): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (currentDepth === targetDepth && node.children.length > 0) {
      ids.push(node.id);
    }
    if (node.children.length > 0) {
      ids.push(
        ...collectNodeIdsAtTreeDepth(
          node.children,
          targetDepth,
          currentDepth + 1,
        ),
      );
    }
  }
  return ids;
}

export function getNodeSelectionState(
  node: CrawlUrlTreeNode,
  selectedNodeIds: Set<string>,
): { checked: boolean; indeterminate: boolean } {
  if (node.children.length === 0) {
    return {
      checked: selectedNodeIds.has(node.id),
      indeterminate: false,
    };
  }

  const realIdsInSubtree = collectRealDescendantIds(node);

  if (realIdsInSubtree.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const selectedCount = realIdsInSubtree.filter((id) =>
    selectedNodeIds.has(id),
  ).length;

  if (node.isSynthetic) {
    return {
      checked: selectedCount > 0,
      indeterminate: false,
    };
  }

  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === realIdsInSubtree.length) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
}
